/**
 * AppIntegrityPage.tsx — Admin → App Integrity (UI v2, 2026-07-31).
 *
 * Answer-first layout replacing the three-tab split that hid the story:
 *
 *   ┌ HERO: the verdict — all checks passed? · audit-project link · actions ┐
 *   ├ GOLDEN TESTS (one unified table: definition + latest result + golden │
 *   │ state + re-baseline right where a failure shows)   │ RUN HISTORY     │
 *   │                                                    │ AUTOMATION      │
 *   └────────────────────────────────────────────────────┴─────────────────┘
 *
 * Every golden value lives as a real test in the audit project
 * (PRJ-App-Inegrity); the table's source chips deep-link there so an
 * auditor can cross-check any number against the app itself.
 */
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Snackbar, Typography } from '@mui/material';
import { Add, Close, FactCheckOutlined, IosShareOutlined } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  exportBundle,
  fetchFixtures,
  fetchRun,
  fetchRuns,
  fetchScheduleSettings,
  downloadFormulaReport,
  startRun,
  updateSnapshot,
  type IntegrityFixture,
  type IntegrityRun,
  type IntegrityRunDetail,
  type IntegrityRunResult,
  type IntegrityScheduleSettings,
} from '../../../services/appIntegrityService';
import { FixtureDetailDialog } from './FixtureDetailDialog';
import { ImportFromTestDialog } from './ImportFromTestDialog';
import { GoldenTestsTable } from './GoldenTestsTable';
import { IntegrityHero } from './IntegrityHero';
import { RebaselineDialog } from './RebaselineDialog';
import { RunHistoryRail } from './RunHistoryRail';
import { SchedulingPanel } from './SchedulingPanel';

export const AppIntegrityPage = () => {
  const [fixtures, setFixtures] = useState<IntegrityFixture[]>([]);
  const [runs, setRuns] = useState<IntegrityRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<IntegrityRunDetail | null>(null);
  const [schedule, setSchedule] = useState<IntegrityScheduleSettings | null>(null);
  const [starting, setStarting] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [dossierKey, setDossierKey] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const [rebaseline, setRebaseline] = useState<{ key: string; name: string } | null>(null);
  const [rebaselineBusy, setRebaselineBusy] = useState(false);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async (selectRunKey?: string) => {
    try {
      const [fixtureData, runData, scheduleData] = await Promise.all([
        fetchFixtures(),
        fetchRuns(),
        fetchScheduleSettings().catch(() => null),
      ]);
      setFixtures(fixtureData.fixtures);
      setRuns(runData.runs);
      if (scheduleData) setSchedule(scheduleData);
      const targetRun = selectRunKey ?? runData.runs[0]?.IntegrityRunKey;
      setSelectedRun(targetRun ? await fetchRun(targetRun) : null);
    } catch {
      setSnack({ message: 'Failed to load App Integrity data', severity: 'error' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while the selected run executes.
  useEffect(() => {
    if (selectedRun?.run.Status !== 'running') return;
    pollRef.current = window.setInterval(async () => {
      try {
        const detail = await fetchRun(selectedRun.run.IntegrityRunKey);
        setSelectedRun(detail);
        if (detail.run.Status !== 'running') {
          const { runs: freshRuns } = await fetchRuns();
          setRuns(freshRuns);
        }
      } catch {
        /* transient poll errors are fine */
      }
    }, 3000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [selectedRun?.run.IntegrityRunKey, selectedRun?.run.Status]);

  const resultByFixture: Map<string, IntegrityRunResult> = useMemo(() => {
    const map = new Map<string, IntegrityRunResult>();
    for (const result of selectedRun?.results ?? []) map.set(result.IntegrityFixtureKey, result);
    return map;
  }, [selectedRun]);

  const latestRun = runs[0] ?? null;
  const viewingOlderRun =
    selectedRun && latestRun && selectedRun.run.IntegrityRunKey !== latestRun.IntegrityRunKey;
  const totalChecks = useMemo(
    () => (selectedRun?.results ?? []).reduce((n, r) => n + r.checks.length, 0),
    [selectedRun],
  );
  const auditProject = useMemo(() => {
    for (const f of fixtures) {
      const src = f.provenance?.sourceProject;
      if (src) {
        const [id, ...rest] = src.split('—');
        return { id: id.trim(), name: rest.join('—').trim() || id.trim() };
      }
    }
    return null;
  }, [fixtures]);

  const handleRunAll = async () => {
    setStarting(true);
    try {
      const runKey = await startRun();
      setSnack({ message: 'Integrity run started', severity: 'info' });
      await load(runKey);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string; error?: string } } })
        .response?.data?.message ?? 'Failed to start run';
      setSnack({ message, severity: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const handleDownload = async (runKey: string) => {
    if (downloadingKey !== null) return;
    setDownloadingKey(runKey);
    try {
      await downloadFormulaReport(runKey);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleRebaseline = async (justification: string) => {
    if (!rebaseline) return;
    setRebaselineBusy(true);
    try {
      await updateSnapshot(rebaseline.key, justification);
      setRebaseline(null);
      setSnack({ message: 'Golden re-baselined — the next run should be green', severity: 'success' });
      await load(selectedRun?.run.IntegrityRunKey);
    } catch (error) {
      setSnack({
        message: (error as { response?: { data?: { error?: string } } }).response?.data?.error ??
          'Golden re-baseline failed',
        severity: 'error',
      });
    } finally {
      setRebaselineBusy(false);
    }
  };

  const handleExport = async () => {
    try {
      const bundle = await exportBundle();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `app-integrity-fixtures-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnack({ message: 'Export failed', severity: 'error' });
    }
  };

  const isRunning = selectedRun?.run.Status === 'running';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <FactCheckOutlined color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          App Integrity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Golden reference tests, replayed through the live API — proof the calculation pipeline is intact.
        </Typography>
      </Box>

      <IntegrityHero
        latestRun={latestRun}
        totalChecks={totalChecks}
        activeFixtureCount={fixtures.filter((f) => f.IsActive).length}
        auditProject={auditProject}
        schedule={schedule}
        running={starting || isRunning}
        downloading={downloadingKey !== null}
        onRunAll={handleRunAll}
        onDownloadPdf={() => latestRun && void handleDownload(latestRun.IntegrityRunKey)}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              Golden tests ({fixtures.length})
            </Typography>
            <Button size="small" startIcon={<Add />} onClick={() => setImportOpen(true)}>
              Add golden test
            </Button>
            <Button size="small" startIcon={<IosShareOutlined />} onClick={handleExport}>
              Export bundle
            </Button>
          </Box>
          {viewingOlderRun && (
            <Alert
              severity="info"
              sx={{ mb: 1, py: 0 }}
              action={
                <Button size="small" onClick={() => void load()}>
                  Show latest
                </Button>
              }
            >
              Viewing results of the run from {new Date(selectedRun!.run.StartedAt).toLocaleString()}.
            </Alert>
          )}
          <GoldenTestsTable
            fixtures={fixtures}
            resultByFixture={resultByFixture}
            onOpenDossier={setDossierKey}
            onRebaseline={(key, name) => setRebaseline({ key, name })}
          />
        </Box>
        <RunHistoryRail
          runs={runs}
          selectedRunKey={selectedRun?.run.IntegrityRunKey ?? null}
          downloadingKey={downloadingKey}
          schedule={schedule}
          onSelectRun={(key) => void fetchRun(key).then(setSelectedRun)}
          onDownloadPdf={(key) => void handleDownload(key)}
          onOpenScheduling={() => setSchedulingOpen(true)}
        />
      </Box>

      {/* Fixture dossier (full-screen) */}
      <FixtureDetailDialog
        fixtureKey={dossierKey}
        latestResult={dossierKey ? resultByFixture.get(dossierKey) : undefined}
        onClose={() => setDossierKey(null)}
        onChanged={() => load(selectedRun?.run.IntegrityRunKey)}
        notify={(message, severity) => setSnack({ message, severity })}
      />

      <ImportFromTestDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(message) => {
          setImportOpen(false);
          setSnack({ message, severity: 'success' });
          void load(selectedRun?.run.IntegrityRunKey);
        }}
        notify={(message, severity) => setSnack({ message, severity })}
      />

      {/* Scheduling (full settings) */}
      <Dialog open={schedulingOpen} onClose={() => setSchedulingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Scheduled runs & alerts
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setSchedulingOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SchedulingPanel
            notify={(message, severity) => {
              setSnack({ message, severity });
              void fetchScheduleSettings().then(setSchedule).catch(() => undefined);
            }}
          />
        </DialogContent>
      </Dialog>

      {rebaseline && (
        <RebaselineDialog
          open
          fixtureName={rebaseline.name}
          busy={rebaselineBusy}
          onClose={() => setRebaseline(null)}
          onConfirm={handleRebaseline}
        />
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack?.severity ?? 'info'} onClose={() => setSnack(null)}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
